import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';
import { useAuthModal } from '../contexts/AuthContext';
import { getCourseById, getStudyById, getOrganization } from '../services/firebase';
import { Course, Study, UserProfile, Organization } from '../types';
import { formatAuthorName } from '../utils/formatters';

interface CoursePortalProps {
  user: UserProfile | null;
  organization: Organization | null;
}

export const CoursePortal: React.FC<CoursePortalProps> = ({ user, organization }) => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();

  const [course, setCourse] = useState<Course | null>(null);
  const [studies, setStudies] = useState<Study[]>([]);
  const [courseOrg, setCourseOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate progress
  const completedStudyIds = user?.completedStudyIds || [];
  const completedInCourse = studies.filter(s => completedStudyIds.includes(s.id)).length;
  const progressPercent = studies.length > 0 ? Math.round((completedInCourse / studies.length) * 100) : 0;
  const isStarted = completedInCourse > 0;
  const isCompleted = studies.length > 0 && completedInCourse === studies.length;

  // Find next study to take
  const nextStudyIndex = studies.findIndex(s => !completedStudyIds.includes(s.id));
  const nextStudy = nextStudyIndex >= 0 ? studies[nextStudyIndex] : null;
  const currentStudyIndex = user?.courseProgress?.[courseId || ''] ?? 0;

  useEffect(() => {
    const loadCourse = async () => {
      if (!courseId) {
        setError('Course not found');
        setLoading(false);
        return;
      }

      try {
        const courseData = await getCourseById(courseId);
        if (!courseData) {
          setError('Course not found');
          setLoading(false);
          return;
        }

        setCourse(courseData);

        // Load organization info
        if (courseData.orgId) {
          const org = await getOrganization(courseData.orgId);
          setCourseOrg(org);
        }

        // Load studies
        if (courseData.studyIds && courseData.studyIds.length > 0) {
          const studyPromises = courseData.studyIds.map(id => getStudyById(id));
          const studyResults = await Promise.all(studyPromises);
          const validStudies = studyResults.filter((s): s is Study => s !== null);
          setStudies(validStudies);
        }
      } catch (err) {
        console.error('Error loading course:', err);
        setError('Failed to load course');
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [courseId]);

  const handleStartCourse = () => {
    if (!user) {
      openAuthModal('signup');
      return;
    }

    if (nextStudy) {
      navigate(`/course/${courseId}`);
    } else if (studies.length > 0) {
      // All completed, restart from beginning
      navigate(`/course/${courseId}`);
    }
  };

  const handleContinueCourse = () => {
    navigate(`/course/${courseId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Icons.Loader className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Course Not Found</h2>
          <p className="text-gray-500 mb-6">{error || 'This course may have been removed.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-blue-800 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          {course.coverImage ? (
            <img
              src={course.coverImage}
              alt=""
              className="w-full h-full object-cover"
              style={{
                objectPosition: course.coverImagePosition
                  ? `${course.coverImagePosition.x}% ${course.coverImagePosition.y}%`
                  : 'center'
              }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary via-blue-700 to-indigo-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30" />
        </div>

        {/* Header */}
        <div className="relative z-10 px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate(user ? '/dashboard' : '/')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <Icons.ChevronLeft className="w-5 h-5" />
              <span className="font-medium">{user ? 'Dashboard' : 'Home'}</span>
            </button>

            {courseOrg && (
              <div className="flex items-center gap-2 text-white/80">
                <Icons.Church className="w-4 h-4" />
                <span className="text-sm font-medium">{courseOrg.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 px-6 pt-12 pb-20 lg:pt-20 lg:pb-32">
          <div className="max-w-4xl mx-auto">
            {/* Course Badge */}
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-sm font-semibold border border-white/20">
                {studies.length} {studies.length === 1 ? 'Study' : 'Studies'}
              </span>
              {course.tags && course.tags[0] && (
                <span className="bg-primary/80 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-sm font-semibold">
                  {course.tags[0]}
                </span>
              )}
              {isCompleted && (
                <span className="bg-green-500/90 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1">
                  <Icons.Check className="w-4 h-4" /> Completed
                </span>
              )}
            </div>

            {/* Title & Description */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6 leading-tight">
              {course.title}
            </h1>

            {course.description && (
              <p className="text-xl text-white/80 mb-8 max-w-2xl leading-relaxed">
                {course.description}
              </p>
            )}

            {/* Progress Bar (if started) */}
            {user && isStarted && !isCompleted && (
              <div className="mb-8 max-w-md">
                <div className="flex items-center justify-between text-sm text-white/80 mb-2">
                  <span>{completedInCourse} of {studies.length} completed</span>
                  <span className="font-bold">{progressPercent}%</span>
                </div>
                <div className="h-3 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
                  <div
                    className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              {!user ? (
                <>
                  <button
                    onClick={() => openAuthModal('signup')}
                    className="px-8 py-4 bg-white text-primary text-lg font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all hover:-translate-y-0.5 flex items-center gap-3 group"
                  >
                    <Icons.User className="w-5 h-5" />
                    Sign Up to Start
                    <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => openAuthModal('login')}
                    className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-bold rounded-xl border border-white/30 hover:bg-white/20 transition-all"
                  >
                    Already have an account? Log in
                  </button>
                </>
              ) : isCompleted ? (
                <button
                  onClick={handleContinueCourse}
                  className="px-8 py-4 bg-white text-primary text-lg font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all hover:-translate-y-0.5 flex items-center gap-3 group"
                >
                  <Icons.Play className="w-5 h-5" />
                  Review Course
                  <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : isStarted ? (
                <button
                  onClick={handleContinueCourse}
                  className="px-8 py-4 bg-white text-primary text-lg font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all hover:-translate-y-0.5 flex items-center gap-3 group"
                >
                  <Icons.Play className="w-5 h-5" />
                  Continue Learning
                  <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <button
                  onClick={handleStartCourse}
                  className="px-8 py-4 bg-white text-primary text-lg font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all hover:-translate-y-0.5 flex items-center gap-3 group"
                >
                  <Icons.Play className="w-5 h-5" />
                  Start Course
                  <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>

            {/* Author info */}
            <div className="mt-8 flex items-center gap-3 text-white/70">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Icons.User className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm">Created by</p>
                <p className="font-semibold text-white">{formatAuthorName(course.authorName)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-8">
            Course Content
          </h2>

          <div className="space-y-4">
            {studies.map((study, index) => {
              const isStudyCompleted = completedStudyIds.includes(study.id);
              const isCurrent = user && nextStudyIndex === index;
              const isLocked = course.requireSequentialViewing && index > 0 && !completedStudyIds.includes(studies[index - 1].id);

              return (
                <div
                  key={study.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                    isCurrent
                      ? 'bg-primary/5 border-primary shadow-sm'
                      : isStudyCompleted
                      ? 'bg-green-50 border-green-200'
                      : isLocked
                      ? 'bg-gray-50 border-gray-200 opacity-60'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {/* Study Number/Status */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isStudyCompleted
                      ? 'bg-green-500 text-white'
                      : isCurrent
                      ? 'bg-primary text-white'
                      : isLocked
                      ? 'bg-gray-300 text-gray-500'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {isStudyCompleted ? (
                      <Icons.Check className="w-5 h-5" />
                    ) : isLocked ? (
                      <Icons.Lock className="w-4 h-4" />
                    ) : (
                      <span className="font-bold">{index + 1}</span>
                    )}
                  </div>

                  {/* Study Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-bold truncate ${isCurrent ? 'text-primary' : 'text-gray-900'}`}>
                      {study.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {study.segments?.length || 0} segments
                    </p>
                  </div>

                  {/* Status Badge */}
                  {isCurrent && (
                    <span className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-full">
                      Up Next
                    </span>
                  )}
                  {isStudyCompleted && (
                    <Icons.Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          {!user && (
            <div className="mt-12 text-center p-8 bg-gray-50 rounded-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Ready to begin?</h3>
              <p className="text-gray-600 mb-6">Create a free account to track your progress and save your spot.</p>
              <button
                onClick={() => openAuthModal('signup')}
                className="px-8 py-4 bg-primary text-white font-bold rounded-xl hover:bg-blue-800 transition-colors"
              >
                Create Free Account
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Footer Link */}
      <footer className="py-8 px-6 bg-gray-100 text-center">
        <button
          onClick={() => navigate(user ? '/dashboard' : '/')}
          className="text-gray-500 hover:text-primary transition-colors text-sm"
        >
          {user ? `View all content from ${organization?.name || 'your organization'}` : 'Explore more courses and studies'}
        </button>
      </footer>
    </div>
  );
};
